/**
 * The master clock for the attempt review screen.
 *
 * Both recordings are reconstructed from R2 chunks, and the two devices do not
 * produce the same chunks at the same instants — the phone can join late, and
 * either can drop out mid-exam. So neither `<video>` can be the clock. Instead
 * this hook owns a single timeline position in "ms since `attempts.started_at`"
 * and every track slaves itself to it, which is what keeps the two views on the
 * same moment and makes a timeline seek land in both at once.
 *
 * That position is **signed**. t=0 is `attempts.started_at`, but the phone
 * starts recording when it is paired — a minute or so before the exam, through
 * placement, paper check and room scan — so its first chunks sit at negative
 * offsets. The clock therefore runs over `[startMs, endMs]` with `startMs <= 0`
 * rather than `[0, duration]`: clamping that footage to zero would pile the
 * whole setup on top of the first second of the exam, and dropping it would
 * throw away the part of the recording most worth watching if something looks
 * wrong. Playback still *opens* at t=0 — the exam is the default subject — and
 * the setup region sits to the left of it, one click away.
 *
 * The position is **anchored to wall time**, not accumulated from frame deltas:
 * at every play or seek we record (position, `performance.now()`), and the
 * current position is derived from the elapsed time since. That matters because
 * animation frames are not guaranteed — an unfocused or occluded window
 * throttles or stops them entirely — and an accumulator would silently stall
 * while the videos kept playing. Deriving from wall time means a skipped frame
 * costs nothing: the next tick lands on the right instant.
 *
 * Ticks come from `requestAnimationFrame` when the page is being looked at, and
 * from a low-frequency interval as a backstop for when it is not.
 */
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * How often the clock is published to React.
 *
 * The position is exact at all times; this is only how often the playhead and
 * the readout repaint. Re-rendering the review screen 60 times a second is what
 * a page with a question table, a rendered paper and two video elements cannot
 * afford — it saturates the main thread and the tab stops responding. ~12 Hz is
 * smooth for a playhead and leaves the browser room to decode video.
 */
const PUBLISH_INTERVAL_MS = 80;

/** Backstop tick, for when animation frames are throttled or not delivered. */
const FALLBACK_TICK_MS = 100;

export interface PlaybackClock {
  /** Position on the master timeline, in ms from attempt start. May be < 0. */
  timeMs: number;
  /** Left edge of the timeline — `<= 0` when there is pre-start footage. */
  startMs: number;
  /** Right edge of the timeline. */
  endMs: number;
  playing: boolean;
  rate: number;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  /** Jump to an absolute offset. Clamped to the attempt. */
  seek: (ms: number) => void;
  /** Relative jump, for the ±10s controls and the arrow keys. */
  nudge: (deltaMs: number) => void;
  setRate: (rate: number) => void;
}

export function useSyncedPlayback(
  startMs: number,
  endMs: number,
): PlaybackClock {
  // Opens on the exam start, not on the left edge of the setup footage.
  const [timeMs, setTimeMs] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRateState] = useState(1);

  // (position, wall time) the current playback run is measured from.
  const anchorPosRef = useRef(0);
  const anchorAtRef = useRef(0);
  const rateRef = useRef(1);
  const playingRef = useRef(false);
  const startRef = useRef(startMs);
  startRef.current = startMs;
  const endRef = useRef(endMs);
  endRef.current = endMs;

  const lastPublishRef = useRef(0);

  const clamp = useCallback(
    (ms: number) => Math.max(startRef.current, Math.min(endRef.current, ms)),
    [],
  );

  /** The exact position right now, whether or not React has been told. */
  const positionNow = useCallback(() => {
    if (!playingRef.current) return anchorPosRef.current;
    const elapsed = performance.now() - anchorAtRef.current;
    return clamp(anchorPosRef.current + elapsed * rateRef.current);
  }, [clamp]);

  /** Re-base the anchor on the current instant — after a seek or a rate change. */
  const reanchor = useCallback((position: number) => {
    anchorPosRef.current = position;
    anchorAtRef.current = performance.now();
  }, []);

  const publish = useCallback((position: number, force: boolean) => {
    const now = performance.now();
    if (force || now - lastPublishRef.current >= PUBLISH_INTERVAL_MS) {
      lastPublishRef.current = now;
      setTimeMs(position);
    }
  }, []);

  useEffect(() => {
    playingRef.current = playing;
    if (!playing) {
      // Freeze exactly where the clock had got to.
      reanchor(anchorPosRef.current);
      publish(anchorPosRef.current, true);
      return;
    }

    reanchor(anchorPosRef.current);
    let frame = 0;
    let stopped = false;

    const tick = () => {
      if (stopped) return;
      const position = positionNow();
      if (position >= endRef.current) {
        anchorPosRef.current = endRef.current;
        publish(endRef.current, true);
        setPlaying(false);
        return;
      }
      publish(position, false);
    };

    const frameLoop = () => {
      if (stopped) return;
      tick();
      frame = requestAnimationFrame(frameLoop);
    };
    frame = requestAnimationFrame(frameLoop);
    const interval = setInterval(tick, FALLBACK_TICK_MS);

    return () => {
      stopped = true;
      cancelAnimationFrame(frame);
      clearInterval(interval);
      // Keep the position we reached, so pausing does not rewind.
      anchorPosRef.current = positionNow();
    };
  }, [playing, positionNow, publish, reanchor]);

  const seek = useCallback(
    (ms: number) => {
      const position = clamp(ms);
      reanchor(position);
      publish(position, true);
    },
    [clamp, publish, reanchor],
  );

  const nudge = useCallback(
    (deltaMs: number) => seek(positionNow() + deltaMs),
    [positionNow, seek],
  );

  const setRate = useCallback(
    (next: number) => {
      // Re-anchor first, or the elapsed time so far would be rescaled.
      reanchor(positionNow());
      rateRef.current = next;
      setRateState(next);
    },
    [positionNow, reanchor],
  );

  return {
    timeMs,
    startMs,
    endMs,
    playing,
    rate,
    play: useCallback(() => setPlaying(true), []),
    pause: useCallback(() => setPlaying(false), []),
    toggle: useCallback(() => setPlaying((p) => !p), []),
    seek,
    nudge,
    setRate,
  };
}
